import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import './tariff.scss';
import { Button, Icons } from '../../../components/base/BootstrapComponents';
import { getTariffData } from '../reducer';
import {
  deleteDailyTariff,
  loadDailyTariffById,
  loadDailyTariffByIdClear,
  loadDailyTariffList,
} from '../actions';
import PageHeader from '../../../components/base/PageHeader/PageHeader';
import {
  responseLogWithNotification,
  getPaginationOpts,
  getJsonFromStorage,
  useUpdatePeriod,
} from '../../../utils/utils';
import Loader from '../../../components/base/Loader/Loader';
import { getSystemData } from '../../System/reducer';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import { addDailyTariffColumns, dailyTariffColumns } from '../constants';
import Pagination from '../../../components/base/Pagination/Pagination';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import { showGlobalConfirm } from '../../System/actions';
import notificationDecorator from '../../Notifications/notificationDecorator';
import IconBox from '../../../components/common/IconBox';
import DailyTariffForm from './DailyTariffForm';

export default function DailyTariffPage() {
  const dispatch = useDispatch();
  const [isCreate, setCreate] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);

  const {
    dailyTariffList,
    dailyTariffListMeta,
    loadingDailyTariffList,
    currentDailyTariff,
    loadingCurrentDailyTariff,
    loadingCreateDailyTariff,
    loadingEditDailyTariff,
    loadingDeleteDailyTariff,
  } = useSelector(getTariffData);

  const { updatePeriod } = useSelector(getSystemData);

  const initial = getJsonFromStorage('daily-tariff-filter', {});
  const { getValues, watch } = useForm({ defaultValues: { ...initial } });

  const loadData = opts => {
    const filterData = getValues();
    const paginationOpts = getPaginationOpts(dailyTariffListMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter: filterData,
    };
    const p1 = dispatch(loadDailyTariffList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const [setPeriod, destroy] = useUpdatePeriod(loadData, updatePeriod);

  useEffect(() => {
    setPeriod(updatePeriod);
    return destroy;
  }, [updatePeriod]);

  useEffect(() => {
    const subscription = watch(() => { loadData(); });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleCreate = () => {
    setCreate(true);
    setShowForm(true);
  };

  const handleView = item => e => {
    handleClose();
    dispatch(loadDailyTariffById(item.id))
      .then(() => {
        setShowView(true);
      })
      .catch(responseLogWithNotification);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEdit = item => e => {
    handleClose();
    dispatch(loadDailyTariffById(item.id))
      .then(() => {
        setShowForm(true);
      })
      .catch(responseLogWithNotification);
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDelete = item => e => {
    dispatch(showGlobalConfirm({
      confirmTitle: 'Удаление дневного тарифа',
      confirmMessage: `Вы уверены, что хотите удалить дневной тариф ${item.id}?`,
      confirmButtonText: 'Удалить',
    })).then(() => {
      dispatch(deleteDailyTariff(item.id))
        .then(() => {
          loadData();
          notificationDecorator({
            title: 'Удаление дневного тарифа',
            message: 'Успешно',
          });
        })
        .catch(responseLogWithNotification);
    }).catch(() => {});
    e.preventDefault();
    e.stopPropagation();
  };

  const handleClose = () => {
    dispatch(loadDailyTariffByIdClear());
    setShowForm(false);
    setShowView(false);
    setCreate(false);
  };

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={(
        <div className="more-wrapper">
          <Button variant="link" className="w-100 more-button" onClick={handleView(item)}>
            Просмотреть
          </Button>
          <Button variant="link" className="w-100 more-button" onClick={handleEdit(item)}>
            Редактировать
          </Button>
          <Button variant="link" className="w-100 more-button delete" onClick={handleDelete(item)}>
            Удалить
          </Button>
        </div>
      )}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  const renderList = () => (
    <div className="page-container">
      <div className="inputs-box">
        <PageHeader header="Дневные тарифы" />
        <div className="inputs">
          <IconBox icon="plus-add" onClick={handleCreate} />
        </div>
      </div>
      <div className="plp-table g-0">
        <div className="frame">
          <div className="sticky-table-wrapper custom-scroll-theme">
            <CustomizedGrid
              className="plp-grid"
              data={dailyTariffList}
              columns={dailyTariffColumns}
              additionalColumns={addDailyTariffColumns}
              name="daily-tariff-grid"
              settingsFormatter={moreFormatter}
              onDoubleClickByRow={(item, event) => handleView(item)(event)}
            />
          </div>
        </div>
        <Pagination meta={dailyTariffListMeta} list={dailyTariffList} handleChangeMeta={loadData} />
      </div>
    </div>
  );

  const isForm = showForm && (currentDailyTariff || isCreate);
  const isView = showView && currentDailyTariff;

  return (
    <div className="tariff-tab">
      <Loader
        global
        loading={loadingDailyTariffList
          || loadingCurrentDailyTariff
          || loadingCreateDailyTariff
          || loadingEditDailyTariff
          || loadingDeleteDailyTariff}
      />
      {!isForm && !isView ? renderList() : null}
      {!isForm && isView ? <DailyTariffForm hasView handleEdit={handleEdit} handleClose={handleClose} /> : null}
      {isForm && !isView ? <DailyTariffForm loadData={loadData} handleEdit={handleEdit} handleClose={handleClose} /> : null}
    </div>
  );
}
